import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrochip, faDollarSign, faVoteYea, faStop, faPlay, faFilePdf, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf'; // npm install jspdf
import AILightComponent from '../../components/AILightComponent'; // Assuming this is available; adjust path if needed

const MOMNew = () => {
  const [activeTab, setActiveTab] = useState('Features'); // Default to first tab

  // States for additional fields above form
  const [headline, setHeadline] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]); // Default to today: 2025-10-19
  const [description, setDescription] = useState('');
  const [saveInTodo, setSaveInTodo] = useState(false);
  const [tags, setTags] = useState('');

  // States from provided MOMForm
  const [title, setTitle] = useState('');
  const [momTitle, setMomTitle] = useState('');
  const [momType, setMOMType] = useState('Type');
  const [heldOnDate, setHeldOnDate] = useState(new Date());
  const [locationAddress, setLocationAddress] = useState('');
  const [momCommittee, setMomCommittee] = useState('');
  const [momInvitees, setMomInvitees] = useState('');
  const [momPresents, setMomPresents] = useState('');
  const [momExcuses, setMomExcuses] = useState('');
  const [momProceedings, setMomProceedings] = useState('');
  const [momAgenda, setMomAgenda] = useState('');
  const [momChairman, setMomChairman] = useState('');
  const [momConvenor, setMomConvenor] = useState('');
  const [userCommentDetails, setUserCommentDetails] = useState('');
  const [isCheckedAISupport, setIsCheckedAISupport] = useState(false);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);
  const [isConversationalMOM, setIsConversationalMOM] = useState(false);
  const [showVoting, setShowVoting] = useState(false);

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const handleConversationalMOMCheckboxChange = (e) => {
    setIsConversationalMOM(e.target.checked);
  };

  const handleDone = () => {
    // Implement your handling logic here (e.g., submit form, save to backend)
    // console.log('Form submitted:', { headline, formDate, description, saveInTodo, tags, momTitle, momType, /* etc. */ });
  };

  const renderAIComponent = (param1, param2) => {
    return isCheckedAISupport && param2 !== '' && (
      <div className="ml-4 mr-4 mb-2 text-black">
        <AILightComponent param1={param1} param2={param2} />
      </div>
    );
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const url = URL.createObjectURL(audioBlob);
        setRecordedBlob(audioBlob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop()); // Stop the stream
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const clearRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setRecordedBlob(null);
    audioChunksRef.current = [];
  };

  // PDF Export function
  const exportToPDF = () => {
    const doc = new jsPDF();
    let yPosition = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(0, 76, 80); // Dark green
    doc.text('Minutes of Meeting Report', 20, yPosition);
    yPosition += 15;

    // Headline
    if (headline) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`Headline: ${headline}`, 20, yPosition);
      yPosition += 10;
    }

    // Date
    doc.text(`Date: ${formDate}`, 20, yPosition);
    yPosition += 10;

    // Description
    if (description) {
      doc.setFontSize(12);
      const descriptionLines = doc.splitTextToSize(description, 170);
      doc.text(descriptionLines, 20, yPosition);
      yPosition += (descriptionLines.length * 7) + 5;
    }

    // Features
    doc.setFontSize(14);
    doc.text('Features Enabled:', 20, yPosition);
    yPosition += 10;
    doc.setFontSize(10);
    doc.text(`Gen AI Support: ${isCheckedAISupport ? 'Enabled' : 'Disabled'}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Voice Input: ${showMicrophoneAlert ? 'Enabled' : 'Disabled'}`, 20, yPosition);
    yPosition += 7;
    if (showMicrophoneAlert) {
      doc.text(`Conversational MOM: ${isConversationalMOM ? 'Enabled' : 'Disabled'}`, 20, yPosition);
      yPosition += 7;
    }
    doc.text(`Voting: ${showVoting ? 'Enabled' : 'Disabled'}`, 20, yPosition);
    yPosition += 15;

    // MOM Details
    doc.setFontSize(14);
    doc.text('MOM Details:', 20, yPosition);
    yPosition += 10;
    doc.setFontSize(10);
    doc.text(`Type: ${momType}`, 20, yPosition);
    yPosition += 7;
    if (momTitle) {
      doc.text(`Title: ${momTitle}`, 20, yPosition);
      yPosition += 7;
    }
    if (momAgenda) {
      doc.text('Opening Comment:', 20, yPosition);
      yPosition += 7;
      const openingLines = doc.splitTextToSize(momAgenda, 170);
      doc.text(openingLines, 20, yPosition);
      yPosition += (openingLines.length * 7) + 5;
    }
    if (momCommittee) {
      doc.text('MOM Committee:', 20, yPosition);
      yPosition += 7;
      const committeeLines = doc.splitTextToSize(momCommittee, 170);
      doc.text(committeeLines, 20, yPosition);
      yPosition += (committeeLines.length * 7) + 5;
    }
    doc.text(`Held On: ${heldOnDate.toISOString().split('T')[0]}`, 20, yPosition);
    yPosition += 7;
    if (locationAddress) {
      doc.text('Held At:', 20, yPosition);
      yPosition += 7;
      const locationLines = doc.splitTextToSize(locationAddress, 170);
      doc.text(locationLines, 20, yPosition);
      yPosition += (locationLines.length * 7) + 5;
    }
    if (momInvitees) {
      doc.text('Invitees:', 20, yPosition);
      yPosition += 7;
      const inviteesLines = doc.splitTextToSize(momInvitees, 170);
      doc.text(inviteesLines, 20, yPosition);
      yPosition += (inviteesLines.length * 7) + 5;
    }
    if (momPresents) {
      doc.text('Members Present:', 20, yPosition);
      yPosition += 7;
      const presentsLines = doc.splitTextToSize(momPresents, 170);
      doc.text(presentsLines, 20, yPosition);
      yPosition += (presentsLines.length * 7) + 5;
    }
    if (momExcuses) {
      doc.text('Excuses:', 20, yPosition);
      yPosition += 7;
      const excusesLines = doc.splitTextToSize(momExcuses, 170);
      doc.text(excusesLines, 20, yPosition);
      yPosition += (excusesLines.length * 7) + 5;
    }
    if (momProceedings) {
      doc.text('Proceedings:', 20, yPosition);
      yPosition += 7;
      const proceedingsLines = doc.splitTextToSize(momProceedings, 170);
      doc.text(proceedingsLines, 20, yPosition);
      yPosition += (proceedingsLines.length * 7) + 5;
    }
    if (momAgenda) {
      doc.text('Agenda:', 20, yPosition);
      yPosition += 7;
      const agendaLines = doc.splitTextToSize(momAgenda, 170);
      doc.text(agendaLines, 20, yPosition);
      yPosition += (agendaLines.length * 7) + 5;
    }
    if (momChairman) {
      doc.text(`Chairman: ${momChairman}`, 20, yPosition);
      yPosition += 7;
    }
    if (momConvenor) {
      doc.text(`Convenor: ${momConvenor}`, 20, yPosition);
      yPosition += 7;
    }
    if (userCommentDetails) {
      doc.text('Additional Comments:', 20, yPosition);
      yPosition += 7;
      const commentsLines = doc.splitTextToSize(userCommentDetails, 170);
      doc.text(commentsLines, 20, yPosition);
      yPosition += (commentsLines.length * 7) + 5;
    }

    // Tags
    if (tags) {
      doc.text('Tags:', 20, yPosition);
      yPosition += 7;
      const tagLines = doc.splitTextToSize(tags, 170);
      doc.text(tagLines, 20, yPosition);
    }

    // Footer
    doc.setFontSize(8);
    doc.text('Generated by Meetings.Chronicles@Think5Whys.com', 20, 290);

    doc.save(`MOM_${momTitle || 'Report'}_${formDate}.pdf`);
  };

  // Email Sharing function (using mailto: for client-side; backend for full features later)
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Minutes of Meeting: ${momTitle || headline || 'New MOM'}`);
    const body = `
Minutes of Meeting Report - Generated on ${formDate}

${headline ? `Headline: ${headline}\n` : ''}
Description: ${description || 'N/A'}

Features Enabled:
- Gen AI Support: ${isCheckedAISupport ? 'Enabled' : 'Disabled'}
- Voice Input: ${showMicrophoneAlert ? 'Enabled' : 'Disabled'}
${showMicrophoneAlert ? `- Conversational MOM: ${isConversationalMOM ? 'Enabled' : 'Disabled'}\n` : ''}
- Voting: ${showVoting ? 'Enabled' : 'Disabled'}

MOM Details:
Type: ${momType}
${momTitle ? `Title: ${momTitle}\n` : ''}
Held On: ${heldOnDate.toISOString().split('T')[0]}
Held At: ${locationAddress || 'N/A'}
${momCommittee ? `MOM Committee: ${momCommittee}\n` : ''}
Invitees: ${momInvitees || 'N/A'}
Members Present: ${momPresents || 'N/A'}
Excuses: ${momExcuses || 'N/A'}
Chairman: ${momChairman || 'N/A'}
Convenor: ${momConvenor || 'N/A'}

Opening Comment:
${momAgenda ? momAgenda : 'N/A'}

Proceedings:
${momProceedings || 'N/A'}

Agenda:
${momAgenda ? momAgenda : 'N/A'}  // Note: Reusing agenda for opening if same

Additional Comments:
${userCommentDetails || 'N/A'}

Tags: ${tags || 'N/A'}

Powered by: Meetings.Chronicles@Think5Whys.com
    `;
    const mailtoLink = `mailto:?subject=${subject}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  const renderWhatToDo = () => (
  <div className="w-full max-w-md mx-auto bg-gradient-to-b from-green-900 to-green-600 text-white p-5 rounded-2xl shadow-md">
    <h3 className="text-m sm:text-lg font-bold mb-2 text-green-100">
      One of the most comprehensive & complete MOM Platform
    </h3>

    <p className="text-sm sm:text-base font-medium mb-1">
      Useful for :{" "} <span className="text-yellow-200 font-semibold"> Corporate, MSMEs, Business Person, Professionals, Groups, Society, Government, NGOs, Family, Institutions</span>
    </p>

    <p className="text-sm mb-4 leading-relaxed">
      Make your meetings more conversational, data-centric and democratic with
      features like:
    </p>

    <ul className="space-y-2 text-left text-sm sm:text-base pl-4 list-disc">
      {[
        "🎙️ Voice Input instead of typing",
        "💬 Record conversation & text input",
        "🤖 AI standardization of captured information",
        "🗳️ Silent Voting",
        "🗣️ Voice Vote",
        "📄 Create PDF meeting reports",
        "📜 View meeting history",
        "🎧 Listen to MOM instead of reading",
        "🌐 Auto-translate in 30+ languages",
        "🔗 Integrated with RCA & Action Items",
        "📚 Integrated with Tasks",
        "👏 Integrated with Applause",
      ].map((item, i) => (
        <li key={i} className="text-green-50">{item}</li>
      ))}
    </ul>

    <p className="text-xs sm:text-sm italic text-green-100 mt-2 text-center leading-relaxed">
        Think5Whys Global DeepTech Solution
    </p>
    <p className="text-xs sm:text-sm italic text-green-100 mt-2 text-center leading-relaxed">
      <span className="font-bold text-yellow-200">
      We Care For Humanity. </span><br />
      A meeting platform equally useful for visually challenged individuals &
      people who prefer not to write.
    </p>
  </div>
);


  const renderMOMDetails = () => (
    <>
      {/* Additional fields above form */}
      <div className="bg-[#002200] border border-green-400 rounded-lg p-4 mb-4">
        {/* <h3 className="text-xl font-bold mb-4 text-left text-green-400">Quick Setup</h3> */}
        <label className="block text-sm text-green-400 mb-1">MOM Headline</label>
        <input
          type="text"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Enter MOM headline"
          className="w-full p-2 bg-[#1a1a1a] text-white border border-green-400 rounded-md mb-3"
        />
        <label className="block text-sm text-green-400 mb-1">Date</label>
        <input
          type="date"
          value={formDate}
          onChange={(e) => setFormDate(e.target.value)}
          className="w-full p-2 bg-[#1a1a1a] text-white border border-green-400 rounded-md mb-3"
        />
        <label className="block text-sm text-green-400 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter description"
          className="w-full p-2 bg-[#1a1a1a] text-white border border-green-400 rounded-md h-24 resize-y mb-3"
        />
        <label className="flex items-center text-white mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={saveInTodo}
            onChange={(e) => setSaveInTodo(e.target.checked)}
            className="mr-2 accent-green-400"
          />
          Save in TODO
        </label>
        <label className="block text-sm text-green-400 mb-1">Tags/Keywords (comma-separated)</label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="e.g., project, urgent, team"
          className="w-full p-2 bg-[#1a1a1a] text-white border border-green-400 rounded-md mb-3"
        />
      </div>

      {/* Voice Recording Section - Shows when Enable Voice Input is checked */}
      {showMicrophoneAlert && (
        <div className="bg-[#002200] border border-green-400 rounded-lg p-4 mb-4 text-center">
          <h4 className="text-green-400 mb-2">Voice Recording</h4>
          {isRecording ? (
            <>
              <p className="text-red-400 font-bold text-lg mb-4">Recording... 🔴</p>
              <button onClick={stopRecording} className="bg-red-500 text-white p-4 border-none rounded-full cursor-pointer text-2xl mx-2 w-16 h-16 flex items-center justify-center hover:bg-red-600 transition-colors" title="Stop Recording">
                <FontAwesomeIcon icon={faStop} />
              </button>
            </>
          ) : (
            <>
              <button onClick={startRecording} className="bg-green-500 text-white p-4 border-none rounded-full cursor-pointer text-2xl mx-2 w-16 h-16 flex items-center justify-center hover:bg-green-600 transition-colors" title="Start Recording" disabled={!showMicrophoneAlert}>
                <FontAwesomeIcon icon={faMicrophone} />
              </button>
            </>
          )}
          {audioUrl && (
            <div className="mt-4 text-left">
              <p className="text-sm mb-2">Recorded Audio:</p>
              <button onClick={playRecording} className="bg-green-500 text-[#001a00] px-4 py-2 border-none rounded-lg cursor-pointer text-sm mr-2 hover:bg-green-600 transition-colors">
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Play
              </button>
              <button onClick={clearRecording} className="bg-gray-600 text-white px-4 py-2 border-none rounded-lg cursor-pointer text-sm hover:bg-gray-700 transition-colors">Clear</button>
              {/* Optional: Upload or process blob later */}
            </div>
          )}
        </div>
      )}

      {/* Adapted MOMForm with dark green theme */}
      <div className="bg-[#002200] border border-green-400 rounded-lg p-4 mb-4">
        <div className="mb-4">
          <label className="flex items-center text-white cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={isCheckedAISupport}
              onChange={() => setIsCheckedAISupport(!isCheckedAISupport)}
              className="mr-2 accent-green-400"
            />
            Gen AI Support (AI Prompt / Response from ChatGPT)
            <FontAwesomeIcon icon={faMicrochip} className="ml-1 text-green-400 text-lg" />
          </label>
        </div>

        <div className="mb-4">
          <label className="flex items-center text-white cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={showMicrophoneAlert}
              onChange={() => setShowMicrophoneAlert(!showMicrophoneAlert)}
              className="mr-2 accent-green-400"
            />
            Enable Voice Input
            <FontAwesomeIcon icon={faMicrophone} className="ml-4 text-green-400 text-lg" />
          </label>
        </div>

        {showMicrophoneAlert && (
          <label className="flex items-center text-sm text-white ml-4 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={isConversationalMOM}
              onChange={handleConversationalMOMCheckboxChange}
              className="mr-2 accent-green-400"
            />
            Make it a Conversational MOM (Powered by Think5Whys AI Partners)
          </label>
        )}

        <div className="mb-4">
          <label className="flex items-center text-white cursor-pointer">
            <input
              type="checkbox"
              checked={showVoting}
              onChange={() => setShowVoting(!showVoting)}
              className="mr-2 accent-green-400"
            />
            Enable Voting
            <FontAwesomeIcon icon={faVoteYea} className="ml-4 text-green-400 text-lg" />
          </label>
        </div>

        <div className="bg-[#1a1a1a] border-b border-green-400 p-2">
          <label className="block text-green-400 text-sm ml-4 mb-1">Type*:</label>
          <select
            name="momType"
            id="momType"
            value={momType}
            onChange={(e) => setMOMType(e.target.value)}
            className="w-full max-w-xs ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3"
          >
            <option value="customerClientMeeting">Customer / Client Meeting</option>
            <option value="officeMeeting">Office Meeting</option>
            <option value="societyMeeting">Society Meeting</option>
            <option value="vendorSupplierMeeting">Vendor / Supplier Meeting</option>
            <option value="teamMeeting">Team Meeting</option>
            <option value="departmentalMeeting">Departmental Meeting</option>
            <option value="seminar">Seminar</option>
            <option value="personalOooMeeting">Personal O-O-O Meeting</option>
            <option value="businessMeeting">Business Meeting</option>
            <option value="otherMeeting">Other Meeting</option>
            <option value="socialMeeting">Social Meeting</option>
            <option value="trustsMeeting">Trusts Meeting</option>
            <option value="groupMeeting">Group Meeting</option>
          </select>

          <label className="block text-green-400 text-sm ml-4 mb-1">Title*:</label>
          <input
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3"
            value={momTitle}
            onChange={(e) => setMomTitle(e.target.value)}
            placeholder="Input title"
          />
          {renderAIComponent(`Title: ${momTitle}`, userCommentDetails)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Opening Comment:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momAgenda}
            onChange={(e) => setMomAgenda(e.target.value)}
            placeholder="Input opening comment"
          />
          {renderAIComponent("Opening Comment", momAgenda)}

          <label className="block text-green-400 text-sm ml-4 mb-1">MOM Committee:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momCommittee}
            onChange={(e) => setMomCommittee(e.target.value)}
            placeholder="Input MOM Committee"
          />
          {renderAIComponent("MOM Committee", momCommittee)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Held On*:</label>
          <input
            type="date"
            className="w-48 ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3"
            value={heldOnDate.toISOString().split('T')[0]}
            onChange={(e) => setHeldOnDate(new Date(e.target.value))}
          />

          <label className="block text-green-400 text-sm ml-4 mb-1">Held At*:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={locationAddress}
            onChange={(e) => setLocationAddress(e.target.value)}
            placeholder="Input location"
          />
          {renderAIComponent("Held At", locationAddress)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Invitees:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momInvitees}
            onChange={(e) => setMomInvitees(e.target.value)}
            placeholder="Input invitees"
          />
          {renderAIComponent("Invitees", momInvitees)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Members Present:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momPresents}
            onChange={(e) => setMomPresents(e.target.value)}
            placeholder="Input members present"
          />
          {renderAIComponent("Members Present", momPresents)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Excuses:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momExcuses}
            onChange={(e) => setMomExcuses(e.target.value)}
            placeholder="Input excuses"
          />
          {renderAIComponent("Excuses", momExcuses)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Proceedings:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momProceedings}
            onChange={(e) => setMomProceedings(e.target.value)}
            placeholder="Input proceedings"
          />
          {renderAIComponent("Proceedings", momProceedings)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Agenda:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={momAgenda}
            onChange={(e) => setMomAgenda(e.target.value)}
            placeholder="Input agenda"
          />
          {renderAIComponent("Agenda", momAgenda)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Chairman:</label>
          <input
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3"
            value={momChairman}
            onChange={(e) => setMomChairman(e.target.value)}
            placeholder="Input chairman"
          />
          {renderAIComponent("Chairman", momChairman)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Convenor:</label>
          <input
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3"
            value={momConvenor}
            onChange={(e) => setMomConvenor(e.target.value)}
            placeholder="Input convenor"
          />
          {renderAIComponent("Convenor", momConvenor)}

          <label className="block text-green-400 text-sm ml-4 mb-1">Additional Comments:</label>
          <textarea
            className="w-full ml-4 bg-[#1a1a1a] text-white border border-green-400 rounded-md p-2 mb-3 h-32 resize-y"
            value={userCommentDetails}
            onChange={(e) => setUserCommentDetails(e.target.value)}
            placeholder="Input additional comments"
          />
          {renderAIComponent("Additional Comments", userCommentDetails)}
        </div>

        <button onClick={handleDone} className="bg-green-500 text-[#001a00] px-6 py-3 rounded-lg cursor-pointer font-bold mt-4 mr-4">
          Done
        </button>
      </div>
    </>
  );

  const renderPreview = () => (
    <>
      <div className="bg-[#002200] border border-green-400 rounded-lg p-4 mb-4">
        <h2 className="text-xl text-green-400 mb-2 text-center">MOM Preview</h2>
        
        {/* Quick Setup Preview */}
        {/* <h3 className="text-lg text-white mb-2 font-bold">Quick Setup</h3> */}
        {headline && <p className="text-base text-white mb-1 font-bold">Headline: {headline}</p>}
        <p className="text-base text-white mb-1 font-bold">Date: {formDate}</p>
        {description && (
          <>
            <p className="text-base text-white mb-1 font-bold">Description:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{description}</p>
          </>
        )}
        <p className="text-base text-white mb-1 font-bold">Save in TODO: <span className="text-green-400 font-bold">{saveInTodo ? 'Yes' : 'No'}</span></p>
        {tags && (
          <>
            <p className="text-base text-white mb-1 font-bold">Tags:</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {tags.split(',').map((tag, index) => (
                <span key={index} className="bg-green-500 text-[#001a00] px-2 py-1 rounded-md text-xs">{tag.trim()}</span>
              ))}
            </div>
          </>
        )}

        {/* Features Preview */}
        <h3 className="text-lg text-white mt-4 mb-2 font-bold">Features Enabled</h3>
        <p className="text-sm text-green-100 mb-1">Gen AI Support: {isCheckedAISupport ? 'Enabled' : 'Disabled'}</p>
        <p className="text-sm text-green-100 mb-1">Voice Input: {showMicrophoneAlert ? 'Enabled' : 'Disabled'}</p>
        {showMicrophoneAlert && <p className="text-sm text-green-100 mb-1">Conversational MOM: {isConversationalMOM ? 'Enabled' : 'Disabled'}</p>}
        <p className="text-sm text-green-100 mb-1">Voting: {showVoting ? 'Enabled' : 'Disabled'}</p>

        {/* Voice Recording Preview */}
        {showMicrophoneAlert && (
          <>
            <h3 className="text-lg text-white mt-4 mb-2 font-bold">Voice Recording</h3>
            <p className="text-sm text-green-100 mb-2">Status: {audioUrl ? 'Recorded' : 'Not Recorded'}</p>
            {audioUrl && (
              <div className="text-left">
                <button onClick={playRecording} className="bg-green-500 text-[#001a00] px-4 py-2 border-none rounded-lg cursor-pointer text-sm mr-2 hover:bg-green-600 transition-colors">
                  <FontAwesomeIcon icon={faPlay} className="mr-1" /> Play Recording
                </button>
              </div>
            )}
          </>
        )}

        {/* MOM Details Preview */}
        <h3 className="text-lg text-white mt-4 mb-2 font-bold">MOM Details</h3>
        <p className="text-base text-white mb-1 font-bold">Type: {momType}</p>
        {momTitle && <p className="text-base text-white mb-1 font-bold">Title: {momTitle}</p>}
        {momAgenda && (
          <>
            <p className="text-base text-white mb-1 font-bold">Opening Comment:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momAgenda}</p>
          </>
        )}
        {momCommittee && (
          <>
            <p className="text-base text-white mb-1 font-bold">MOM Committee:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momCommittee}</p>
          </>
        )}
        <p className="text-base text-white mb-1 font-bold">Held On: {heldOnDate.toISOString().split('T')[0]}</p>
        {locationAddress && (
          <>
            <p className="text-base text-white mb-1 font-bold">Held At:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{locationAddress}</p>
          </>
        )}
        {momInvitees && (
          <>
            <p className="text-base text-white mb-1 font-bold">Invitees:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momInvitees}</p>
          </>
        )}
        {momPresents && (
          <>
            <p className="text-base text-white mb-1 font-bold">Members Present:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momPresents}</p>
          </>
        )}
        {momExcuses && (
          <>
            <p className="text-base text-white mb-1 font-bold">Excuses:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momExcuses}</p>
          </>
        )}
        {momProceedings && (
          <>
            <p className="text-base text-white mb-1 font-bold">Proceedings:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momProceedings}</p>
          </>
        )}
        {momAgenda && (
          <>
            <p className="text-base text-white mb-1 font-bold">Agenda:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{momAgenda}</p>
          </>
        )}
        {momChairman && <p className="text-base text-white mb-1 font-bold">Chairman: {momChairman}</p>}
        {momConvenor && <p className="text-base text-white mb-1 font-bold">Convenor: {momConvenor}</p>}
        {userCommentDetails && (
          <>
            <p className="text-base text-white mb-1 font-bold">Additional Comments:</p>
            <p className="text-sm text-green-100 mb-2 whitespace-pre-wrap">{userCommentDetails}</p>
          </>
        )}
      </div>
      <div className="text-center">
        <button onClick={exportToPDF} className="bg-green-500 text-[#001a00] px-6 py-3 rounded-lg cursor-pointer font-bold mx-2 inline-block">
          <FontAwesomeIcon icon={faFilePdf} className="mr-2" /> Export to PDF
        </button>
        <button onClick={shareViaEmail} className="bg-green-500 text-[#001a00] px-6 py-3 rounded-lg cursor-pointer font-bold mx-2 inline-block">
          <FontAwesomeIcon icon={faEnvelope} className="mr-2" /> Share via Email
        </button>
      </div>
    </>
  );

  return (
    <div className="bg-black text-white min-h-screen p-4 font-sans box-border">
      <h1 className="text-center mb-1 text-xl text-yellow-200">Minutes of Meeting</h1>
      
      {/* Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-0 mb-1 w-full place-items-center">
        {[
            { label: "Features", emoji: "📝", color: "green-600" },
            { label: "MOM Form", emoji: "📋", color: "green-600" },
            { label: "Preview", emoji: "👀", color: "green-600" },
        ].map((tab) => (
            <button
            key={tab.label}
            onClick={() => setActiveTab(tab.label)}
            className={`flex items-center justify-center gap-2 w-full py-31 rounded-lg border border-green-400 text-sm font-medium transition-all duration-200
                ${
                activeTab === tab.label
                    ? "bg-green-600 text-white"
                    : `bg-${tab.color} text-white hover:bg-green-700`
                }`}
            >
            <span className="text-lg">{tab.emoji}</span>
            <span>{tab.label}</span>
            </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={activeTab === 'Features' ? 'block' : 'hidden'}>
        {renderWhatToDo()}
      </div>
      <div className={activeTab === 'MOM Details' ? 'block' : 'hidden'}>
        {renderMOMDetails()}
      </div>
      <div className={activeTab === 'Preview' ? 'block' : 'hidden'}>
        {renderPreview()}
      </div>
    </div>
  );
};

export default MOMNew;